import { formulaDependencies, validateSafeFormula } from "./safe-formula-engine";
import {
  MIN_NODE_GAP,
  NODE_HEIGHT,
  NODE_WIDTH,
  correctModuleGraph,
  findFreePosition,
} from "./module-builder-layout";

const DEFAULT_VIEWPORT = { x: 80, y: 60, zoom: 1 };
const NODE_GAP = MIN_NODE_GAP;

export const ALLOWED_NODE_TYPES = [
  "service",
  "quantity",
  "productPrice",
  "productCatalog",
  "laborHours",
  "percentage",
  "distribution",
  "formula",
  "subtotal",
  "total",
  "note",
];

const ALLOWED_TYPE_SET = new Set(ALLOWED_NODE_TYPES);
const NODE_REF_RE = /^nodes\.(.+)\.(?:amount|value)$/;
const FORMULA_BUILTIN = new Set(["input", "quantity", "price", "percent"]);

const throwCoded = (code, message) => {
  const err = new Error(message || code);
  err.code = code;
  throw err;
};

/** Strip optional ```json ... ``` wrapper from pasted text. */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const productTitle = (product) =>
  product?.displayTitle ||
  product?.title ||
  product?.name ||
  product?.productName ||
  product?.product_name ||
  "";

const normalizeProductLine = (product, index = 0) => {
  const productId = product?.product_id ?? product?.id ?? null;
  return {
    id: product?.id || productId || `manual-${index}`,
    product_id: productId,
    title: productTitle(product) || "Produkt",
    quantity: product?.quantity ?? 1,
    unitPrice: product?.unitPrice ?? product?.unit_price ?? product?.sales_price ?? 0,
    purchasePrice: product?.purchasePrice ?? product?.purchase_price ?? 0,
    included: product?.included !== false,
  };
};

const normalizeLockedFields = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  return { ...raw };
};

const withInsertOrder = (base, data) => {
  const raw = data.insert_order ?? data.insertOrder ?? data.wizard_order;
  if (raw == null || raw === "") return base;
  const n = Number(raw);
  if (!Number.isFinite(n)) return base;
  return { ...base, insert_order: n };
};

const normalizeNodeData = (type, rawData) => {
  const data = rawData && typeof rawData === "object" && !Array.isArray(rawData) ? rawData : {};
  const lockedFields = normalizeLockedFields(data.lockedFields);

  let normalized;
  switch (type) {
    case "service":
      normalized = {
        amount: toNumber(data.amount),
        enabled: data.enabled !== false,
        hours: toNumber(data.hours),
        hourlyRate: toNumber(data.hourlyRate ?? data.hourly_rate),
        costHourlyRate: toNumber(data.costHourlyRate ?? data.cost_hourly_rate),
        hourRateId: data.hourRateId ?? data.hour_rate_id ?? null,
        lockedFields,
      };
      break;
    case "quantity":
      normalized = {
        quantity: toNumber(data.quantity, 1),
        unitPrice: toNumber(data.unitPrice),
        lockedFields,
      };
      break;
    case "productPrice":
      normalized = {
        quantity: toNumber(data.quantity, 1),
        unitPrice: toNumber(data.unitPrice),
        productName: String(data.productName || ""),
        lockedFields,
      };
      break;
    case "productCatalog":
      normalized = {
        products: Array.isArray(data.products) ? data.products.map(normalizeProductLine) : [],
        lockedFields,
      };
      break;
    case "laborHours":
      normalized = {
        hours: toNumber(data.hours, 1),
        hourlyRate: toNumber(data.hourlyRate),
        costHourlyRate: toNumber(data.costHourlyRate),
        lockedFields,
      };
      break;
    case "percentage":
      normalized = {
        percent: toNumber(data.percent),
        scope: String(data.scope || "all") || "all",
        percentMode: data.percentMode === "discount" ? "discount" : "markup",
        lockedFields,
      };
      break;
    case "distribution":
      normalized = {
        divisor: toNumber(data.divisor, 2),
        enabled: data.enabled !== false,
        lockedFields,
      };
      break;
    case "formula": {
      const next = {
        expression: String(data.expression ?? "input").trim() || "input",
        enabled: data.enabled !== false,
        lockedFields,
      };
      if (data.quantity != null && data.quantity !== "") next.quantity = toNumber(data.quantity);
      if (data.price != null && data.price !== "") next.price = toNumber(data.price);
      if (data.percent != null && data.percent !== "") next.percent = toNumber(data.percent);
      normalized = next;
      break;
    }
    case "note":
      normalized = { note: String(data.note || ""), lockedFields };
      break;
    default:
      normalized = { lockedFields };
  }
  return withInsertOrder(normalized, data);
};

const validateFormulaExpression = (expression, nodeIds) => {
  const result = validateSafeFormula(expression);
  if (!result.ok) {
    throwCoded("unsafe_formula", result.error || "unsafe_formula");
  }

  (result.dependencies || []).forEach((name) => {
    if (FORMULA_BUILTIN.has(name)) return;
    if (String(name).startsWith("globals.")) return;
    const match = String(name).match(NODE_REF_RE);
    if (match && nodeIds.has(match[1])) return;
    throwCoded("unsafe_formula", `unknown_variable:${name}`);
  });
};

const formulaNodeRefs = (expression) =>
  formulaDependencies(expression)
    .map((name) => {
      const match = String(name).match(NODE_REF_RE);
      return match ? match[1] : null;
    })
    .filter(Boolean);

export function graphHasCycle(nodes = [], edges = []) {
  const outgoing = new Map();
  const addEdge = (from, to) => {
    if (!from || !to) return;
    if (!outgoing.has(from)) outgoing.set(from, []);
    outgoing.get(from).push(to);
  };

  edges.forEach((edge) => addEdge(edge.source, edge.target));
  nodes.forEach((node) => {
    if (node?.type !== "formula") return;
    formulaNodeRefs(node.data?.expression).forEach((refId) => addEdge(refId, node.id));
  });

  const visiting = new Set();
  const visited = new Set();

  const visit = (id) => {
    if (visited.has(id)) return false;
    if (visiting.has(id)) return true;
    visiting.add(id);
    for (const next of outgoing.get(id) || []) {
      if (visit(next)) return true;
    }
    visiting.delete(id);
    visited.add(id);
    return false;
  };

  return (nodes || []).some((node) => visit(node.id));
}

export { findFreePosition };

const uniqueId = (prefix, used) => {
  let suffix = 1;
  let candidate = `${prefix}-${suffix}`;
  while (used.has(candidate)) {
    suffix += 1;
    candidate = `${prefix}-${suffix}`;
  }
  used.add(candidate);
  return candidate;
};

const remapFormulaExpression = (expression, idMap) =>
  String(expression || "").replace(/nodes\.([A-Za-z0-9_-]+)\.(amount|value)/g, (full, id, field) => {
    const nextId = idMap[id];
    return nextId ? `nodes.${nextId}.${field}` : full;
  });

const normalizeViewport = (raw) => {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_VIEWPORT };
  return {
    x: toNumber(raw.x, DEFAULT_VIEWPORT.x),
    y: toNumber(raw.y, DEFAULT_VIEWPORT.y),
    zoom: toNumber(raw.zoom, DEFAULT_VIEWPORT.zoom) || 1,
  };
};

const normalizeGlobals = (raw) => {
  if (!Array.isArray(raw)) return undefined;
  const globals = raw
    .filter((item) => item && typeof item === "object" && String(item.key || "").trim())
    .map((item) => ({
      key: String(item.key).trim(),
      label: item.label != null ? String(item.label) : undefined,
      value: item.value,
      unit: item.unit != null ? String(item.unit) : undefined,
      ask_on_use: item.ask_on_use === false ? false : true,
    }));
  return globals.length ? globals : undefined;
};

const normalizeRules = (raw) => {
  if (!Array.isArray(raw)) return undefined;
  const rules = raw
    .filter((item) => item && typeof item === "object")
    .map((item, index) => ({
      id: String(item.id || `rule-${index}`),
      when: {
        global: String(item.when?.global || "").trim(),
        equals: item.when?.equals ?? "",
      },
      then: {
        action: ["disable_node", "enable_node"].includes(item.then?.action)
          ? item.then.action
          : "disable_node",
        node_id: String(item.then?.node_id || "").trim(),
      },
    }))
    .filter((rule) => rule.when.global && rule.then.node_id);
  return rules.length ? rules : undefined;
};

const unwrapGraphPayload = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  if (Array.isArray(data.nodes)) return data;
  if (data.module_graph && Array.isArray(data.module_graph.nodes)) return data.module_graph;
  if (data.module_graph_json && typeof data.module_graph_json === "object") {
    return unwrapGraphPayload(data.module_graph_json);
  }
  return null;
};

const normalizeImportedGraph = (raw) => {
  const payload = unwrapGraphPayload(raw);
  if (!payload) throwCoded("invalid_graph", "invalid_graph");

  const rawNodes = Array.isArray(payload.nodes) ? payload.nodes : [];
  if (!rawNodes.length) throwCoded("no_nodes", "no_nodes");

  const nodes = rawNodes.map((rawNode, index) => {
    if (!rawNode || typeof rawNode !== "object") throwCoded("invalid_graph", "invalid_node");
    const id = String(rawNode.id || "").trim();
    if (!id) throwCoded("missing_id", `missing_id:${index}`);
    const type = String(rawNode.type || "").trim();
    if (!ALLOWED_TYPE_SET.has(type)) throwCoded("unknown_type", `unknown_type:${type || "?"}`);
    return {
      id,
      type,
      title: String(rawNode.title || type),
      x: toNumber(rawNode.x, 80),
      y: toNumber(rawNode.y, 120 + index * (NODE_HEIGHT + NODE_GAP)),
      data: normalizeNodeData(type, rawNode.data),
    };
  });

  const nodeIds = new Set();
  nodes.forEach((node) => {
    if (nodeIds.has(node.id)) throwCoded("missing_id", `duplicate_id:${node.id}`);
    nodeIds.add(node.id);
  });

  nodes.forEach((node) => {
    if (node.type === "formula") {
      validateFormulaExpression(node.data.expression, nodeIds);
    }
  });

  const edges = (Array.isArray(payload.edges) ? payload.edges : []).map((rawEdge, index) => {
    if (!rawEdge || typeof rawEdge !== "object") throwCoded("invalid_graph", "invalid_edge");
    const source = String(rawEdge.source || "").trim();
    const target = String(rawEdge.target || "").trim();
    if (!source || !target || !nodeIds.has(source) || !nodeIds.has(target)) {
      throwCoded("missing_edge_target", `missing_edge_target:${index}`);
    }
    return {
      id: String(rawEdge.id || "").trim() || `edge-${source}-${target}-${index}`,
      source,
      target,
      operation: rawEdge.operation === "subtract" ? "subtract" : "add",
    };
  });

  if (graphHasCycle(nodes, edges)) throwCoded("circular", "circular");

  const graph = {
    version: 1,
    viewport: normalizeViewport(payload.viewport),
    nodes,
    edges,
  };

  const globals = normalizeGlobals(payload.globals);
  if (globals) graph.globals = globals;
  const rules = normalizeRules(payload.rules);
  if (rules) graph.rules = rules;
  if (typeof payload.offer_text === "string") graph.offer_text = payload.offer_text;
  if (payload.ui && typeof payload.ui === "object") {
    graph.ui = {
      editor_mode: payload.ui.editor_mode === "advanced" ? "advanced" : "simple",
    };
  }

  return graph;
};

/**
 * Parse pasted string into a module_graph. Accepts a full graph or `{ nodes, edges }`.
 * Rejects unknown types, missing ids, dangling edges, unsafe formulas, and cycles.
 */
export function parseImportedModuleGraphJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) throwCoded("empty", "empty");

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    throwCoded("invalid_json", "invalid_json");
  }

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      throwCoded("invalid_json", "invalid_json");
    }
  }

  return normalizeImportedGraph(data);
}

export function serializeModuleGraphForExport(graph = {}) {
  const payload = {
    version: 1,
    viewport: normalizeViewport(graph.viewport),
    nodes: Array.isArray(graph.nodes)
      ? graph.nodes.map((node) => ({
          id: node.id,
          type: node.type,
          title: node.title || "",
          x: toNumber(node.x),
          y: toNumber(node.y),
          data: node.data || {},
        }))
      : [],
    edges: Array.isArray(graph.edges)
      ? graph.edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          operation: edge.operation === "subtract" ? "subtract" : "add",
        }))
      : [],
  };
  const globals = normalizeGlobals(graph.globals);
  if (globals) payload.globals = globals;
  const rules = normalizeRules(graph.rules);
  if (rules) payload.rules = rules;
  if (typeof graph.offer_text === "string" && graph.offer_text.trim()) {
    payload.offer_text = graph.offer_text;
  }
  if (graph.ui?.editor_mode) {
    payload.ui = { editor_mode: graph.ui.editor_mode === "advanced" ? "advanced" : "simple" };
  }
  return JSON.stringify(payload, null, 2);
}

const assignFreshIds = (graph, reservedIds = new Set()) => {
  const used = new Set(reservedIds);
  const idMap = {};

  const nodes = (graph.nodes || []).map((node) => {
    const nextId = used.has(node.id) || !node.id ? uniqueId(node.type || "node", used) : node.id;
    used.add(nextId);
    idMap[node.id] = nextId;
    return { ...node, id: nextId };
  });

  const remappedNodes = nodes.map((node) => {
    if (node.type !== "formula") return node;
    return {
      ...node,
      data: {
        ...node.data,
        expression: remapFormulaExpression(node.data?.expression, idMap),
      },
    };
  });

  const usedEdgeIds = new Set(used);
  const edges = (graph.edges || []).map((edge, index) => {
    const source = idMap[edge.source] || edge.source;
    const target = idMap[edge.target] || edge.target;
    let id = edge.id && !usedEdgeIds.has(edge.id) ? edge.id : "";
    if (!id) id = uniqueId(`edge-${source}-${target}`, usedEdgeIds);
    usedEdgeIds.add(id);
    return {
      id,
      source,
      target,
      operation: edge.operation === "subtract" ? "subtract" : "add",
      _index: index,
    };
  });

  return {
    ...graph,
    nodes: remappedNodes,
    edges: edges.map(({ _index, ...edge }) => edge),
  };
};

const placeWithoutOverlap = (nodes, existingNodes = []) => {
  const placed = [];
  nodes.forEach((node, index) => {
    const pos = findFreePosition(
      Number.isFinite(Number(node.x)) ? Number(node.x) : 80,
      Number.isFinite(Number(node.y)) ? Number(node.y) : 120 + index * (NODE_HEIGHT + NODE_GAP),
      [...existingNodes, ...placed],
      node.id
    );
    placed.push({ ...node, ...pos });
  });
  return placed;
};

const existingShiftX = (nodes = []) => {
  if (!nodes.length) return 0;
  const maxX = Math.max(...nodes.map((node) => toNumber(node.x)));
  return maxX + NODE_WIDTH + NODE_GAP * 2;
};

const mergeGlobals = (current, incoming) => {
  const byKey = new Map();
  (current || []).forEach((item) => {
    if (item?.key) byKey.set(item.key, item);
  });
  (incoming || []).forEach((item) => {
    if (item?.key && !byKey.has(item.key)) byKey.set(item.key, item);
  });
  return byKey.size ? [...byKey.values()] : undefined;
};

/**
 * Apply parsed graph onto the current editor graph.
 * replace: use imported graph (fresh ids if colliding within the set).
 * append: merge nodes/edges, always assign fresh ids, place without overlap.
 */
export function applyImportedModuleGraph(currentGraph, importedGraph, mode = "replace") {
  const incoming = importedGraph && typeof importedGraph === "object" ? importedGraph : { nodes: [], edges: [] };

  if (mode !== "append") {
    const unique = assignFreshIds(incoming, new Set());
    return correctModuleGraph({
      ...unique,
      version: 1,
      viewport: unique.viewport || currentGraph?.viewport || DEFAULT_VIEWPORT,
      nodes: placeWithoutOverlap(unique.nodes, []),
    });
  }

  const existingNodes = Array.isArray(currentGraph?.nodes) ? currentGraph.nodes : [];
  const existingEdges = Array.isArray(currentGraph?.edges) ? currentGraph.edges : [];
  const reserved = new Set([
    ...existingNodes.map((node) => node.id),
    ...existingEdges.map((edge) => edge.id),
  ]);
  const shiftX = existingShiftX(existingNodes);
  const shifted = {
    ...incoming,
    nodes: (incoming.nodes || []).map((node) => ({
      ...node,
      x: toNumber(node.x) + shiftX,
    })),
  };
  const unique = assignFreshIds(shifted, reserved);
  const placed = placeWithoutOverlap(unique.nodes, existingNodes);
  const globals = mergeGlobals(currentGraph?.globals, unique.globals);

  const next = {
    version: 1,
    viewport: currentGraph?.viewport || unique.viewport || DEFAULT_VIEWPORT,
    nodes: [...existingNodes, ...placed],
    edges: [...existingEdges, ...(unique.edges || [])],
  };
  if (currentGraph?.ui) next.ui = currentGraph.ui;
  else if (unique.ui) next.ui = unique.ui;
  if (globals) next.globals = globals;
  return correctModuleGraph(next);
}

export const SAMPLE_MODULE_GRAPH = {
  version: 1,
  viewport: { ...DEFAULT_VIEWPORT },
  nodes: [
    {
      id: "service-apartment",
      type: "service",
      title: "Leilighet",
      x: 80,
      y: 120,
      data: { amount: 4500, enabled: true, lockedFields: { amount: true } },
    },
    {
      id: "kilometers",
      type: "quantity",
      title: "Antall kilometer",
      x: 80,
      y: 360,
      data: { quantity: 18, unitPrice: 25, lockedFields: { unitPrice: true } },
    },
    {
      id: "job-percent",
      type: "percentage",
      title: "Påslag hele jobben",
      x: 390,
      y: 210,
      data: { percent: 12, scope: "all" },
    },
    {
      id: "distribution",
      type: "distribution",
      title: "Fordeling på X",
      x: 680,
      y: 210,
      data: { divisor: 2 },
    },
    {
      id: "total",
      type: "total",
      title: "Full service total",
      x: 950,
      y: 250,
      data: { lockedFields: {} },
    },
  ],
  edges: [
    { id: "edge-service-percent", source: "service-apartment", target: "job-percent", operation: "add" },
    { id: "edge-km-percent", source: "kilometers", target: "job-percent", operation: "add" },
    { id: "edge-percent-distribution", source: "job-percent", target: "distribution", operation: "add" },
    { id: "edge-distribution-total", source: "distribution", target: "total", operation: "add" },
  ],
};

export const SAMPLE_MODULE_GRAPH_JSON = JSON.stringify(SAMPLE_MODULE_GRAPH, null, 2);
